import { Feature } from 'ol';
import EsriJSON from 'ol/format/EsriJSON';
import GeometryLayout from 'ol/geom/GeometryLayout';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { async_forEach, chunk } from '../../../misc/array';
import { GetToken } from '../../../misc/authentication/Authentication';
import { Maybe, None, Some } from '../../../misc/option';
import { EPSG } from '../../../projection';
import { DistanceUnit } from '../../../../impl/layers/client/BufferLayer';
export class FeatureQueryManager {
    constructor(opts, identifier) {
        this.opts = opts;
        this.identifier = identifier;
        this.projection = EPSG(3857);
        this.reader = new EsriJSON();
    }
    async _make_post_request(params = {}, request_url = '') {
        var _a;
        const url = request_url ? request_url : this.opts.url;
        let fillUrl = new URL(url.slice(0, 4) === 'http' ? url + '?' : 'https:' + url + '?');
        const serialize = (obj) => {
            let strs = [];
            for (let p in obj) {
                if (obj.hasOwnProperty(p)) {
                    strs.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
                }
            }
            return strs.join('&');
        };
        if (this.opts.authRequired) {
            var tokenResponse = await GetToken({
                name: request_url ? "BufferUrl" : "ParcelQueryUrl",
                serviceUrl: url,
                authUser: this.opts.authUser,
                authPassword: this.opts.authPassword
            });
            if (tokenResponse.token) {
                fillUrl.searchParams.append('token', tokenResponse.token);
            }
            else {
                console.error((_a = tokenResponse.error) === null || _a === void 0 ? void 0 : _a.details);
            }
        }
        const response = fetch(fillUrl.toString(), {
            method: "POST",
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json'
            },
            redirect: 'follow',
            body: serialize(params)
        }).catch((reason) => { throw reason; });
        return response;
    }
    _process_response(response) {
        const features = this.reader.readFeatures(response, { featureProjection: this.projection });
        if (features.length > 0)
            return new Some(features);
        return None.instance();
    }
    async _request_features_by_ids(ids) {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        if (this.opts.parcel_query_urls && this.opts.parcel_query_urls.length) {
            const urls = [...this.opts.parcel_query_urls];
            const features = [];
            for (const url of urls) {
                const request = this._make_post_request({
                    f: 'json',
                    inSR: this.projection.split(':')[1],
                    outSR: this.projection.split(':')[1],
                    returnGeometry: true,
                    outfields: url.idfield,
                    where: url.idfield + " IN ('" + (ids ? ids.join("','") : "") + "')"
                }, url.url);
                let response = await request;
                if (response.ok) {
                    let json = await response.json();
                    let processed_features = this._process_response(json);
                    if (processed_features.isSome()) {
                        features.push(processed_features);
                    }
                }
                else {
                    continue;
                }
            }
            if (features.length) {
                let flattenedFeatures = features
                    .filter(opt => opt.isSome())
                    .flatMap(opt => opt.unwrap());
                return new Some(flattenedFeatures);
            }
            return None.instance();
        }
        else {
            const request = this._make_post_request({
                f: 'json',
                inSR: this.projection.split(':')[1],
                outSR: this.projection.split(':')[1],
                returnGeometry: true,
                outfields: this.identifier,
                where: this.identifier + " IN ('" + (ids ? ids.join("','") : "") + "')"
            });
            let r1 = await request.then(r => r.json());
            return this._process_response(r1);
        }
    }
    async _request_feature_at_point(point) {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        if (this.opts.parcel_query_urls && this.opts.parcel_query_urls.length) {
            const urls = [...this.opts.parcel_query_urls].reverse();
            for (const url of urls) {
                const buffer_distance = 10 * 0.3048;
                const xmin = point[0] - buffer_distance;
                const xmax = point[0] + buffer_distance;
                const ymin = point[1] - buffer_distance;
                const ymax = point[1] + buffer_distance;
                const request = this._make_post_request({
                    f: 'json',
                    geometry: `{
            "xmin": ${xmin},
            "ymin": ${ymin},
            "xmax": ${xmax},
            "ymax": ${ymax}
          }`,
                    geometryType: 'esriGeometryEnvelope',
                    spatialRel: 'esriSpatialRelIntersects',
                    returnGeometry: true,
                    outfields: url.idfield,
                    inSR: this.projection.split(':')[1],
                    outSR: this.projection.split(':')[1]
                }, url.url);
                let response = await request;
                if (response.ok) {
                    let json = await response.json();
                    let processed_features = this._process_response(json);
                    if (processed_features.isSome()) {
                        return processed_features.map(fs => fs[0]);
                    }
                }
                else {
                    continue;
                }
            }
            return None.instance();
        }
        else {
            const request = this._make_post_request({
                f: 'json',
                geometry: point[0] + ',' + point[1],
                geometryType: 'esriGeometryPoint',
                spatialRel: 'esriSpatialRelIntersects',
                returnGeometry: true,
                outfields: this.identifier,
                inSR: this.projection.split(':')[1],
                outSR: this.projection.split(':')[1]
            });
            let r1 = await request.then(r => r.json());
            return this._process_response(r1).map(fs => fs[0]);
        }
    }
    async _request_features_by_geometry(geometry, idfield, request_url = '') {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        let identifier = idfield ? idfield : this.identifier;
        let coords = geometry.getCoordinates();
        let coord_strings = coords.map(ring => `[${ring.map(el => `[${el}]`)}]`);
        const params = {
            f: 'json',
            geometry: `{"rings": [${coord_strings.join()}]}`,
            geometryType: 'esriGeometryPolygon',
            spatialRel: 'esriSpatialRelIntersects',
            returnGeometry: true,
            outFields: identifier,
            inSR: this.projection.split(':')[1],
            outSR: this.projection.split(':')[1],
            where: ""
        };
        const request = this._make_post_request(params, request_url);
        const r1 = await request.then(t => t.json());
        let features = this._process_response(r1);
        let addtl = features;
        while (addtl.isSomeAnd(l => l.length > 0)) {
            const current_ids = features.map(flist => flist.map(f => f.get(identifier)));
            params.where = `${identifier} IS NOT NULL AND ${identifier} NOT IN ('${current_ids.unwrapOr([]).join("','")}')`;
            addtl = this._process_response(await this._make_post_request(params, request_url).then(r => r.json()));
            features = features.map(l => {
                l.push(...addtl.unwrapOr([]));
                return l;
            });
        }
        return features;
    }
    async _request_neighbors_by_geometry(geometries = [], request_url = '') {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        if (geometries.length === 0) {
            throw new Error('No geometries supplied.');
        }
        let request_geometry = '';
        let geometryType;
        let spatialRel;
        if (geometries.every(g => g instanceof Polygon)) {
            request_geometry = JSON.stringify({
                rings: geometries.flatMap(g => g.getCoordinates(false))
            });
            geometryType = 'esriGeometryPolygon';
            spatialRel = 'esriSpatialRelTouches';
        }
        else if (geometries.every(g => g instanceof Point)) {
            const points = geometries.map(g => g.getCoordinates());
            if (points.length === 1) {
                request_geometry = JSON.stringify({ x: points[0][0], y: points[0][1] });
                geometryType = 'esriGeometryPoint';
            }
            else {
                request_geometry = JSON.stringify({ points });
                geometryType = 'esriGeometryMultipoint';
            }
            spatialRel = 'esriSpatialRelIntersects';
        }
        else {
            throw new Error('Mixed or unsupported geometry types.');
        }
        let params = {
            f: 'json',
            geometry: request_geometry,
            geometryType: geometryType,
            spatialRel: spatialRel,
            returnGeometry: true,
            outFields: this.identifier,
            inSR: this.projection.split(':')[1],
            outSR: this.projection.split(':')[1],
            where: ""
        };
        const request = this._make_post_request(params, request_url);
        const r1 = await request.then(t => t.json());
        let features = this._process_response(r1);
        return features;
    }
    async _request_buffer_by_geometry(geometries = [], request_url, options) {
        while (!this.projection) {
            await new Promise(res => setTimeout(res, 20));
        }
        if (geometries.length === 0) {
            throw new Error('No geometries supplied.');
        }
        let request_geometry = '';
        if (geometries.every(g => g instanceof Polygon)) {
            request_geometry = JSON.stringify({
                geometries: geometries.map(g => ({
                    rings: g.getCoordinates(false)
                })),
                geometryType: 'esriGeometryPolygon'
            });
        }
        else if (geometries.every(g => g instanceof Point)) {
            request_geometry = JSON.stringify({
                geometries: geometries.map(g => {
                    const coords = g.getCoordinates();
                    return {
                        x: coords[0],
                        y: coords[1]
                    };
                }),
                geometryType: 'esriGeometryPoint'
            });
        }
        else {
            throw new Error('Mixed or unsupported geometry types.');
        }
        let params = {
            f: 'json',
            geometries: request_geometry,
            inSR: this.projection.split(':')[1],
            bufferSR: this.projection.split(':')[1],
            unionResults: true,
            distances: options.distance,
            unit: options.unit === DistanceUnit.METERS ? 9001 : 9002,
        };
        const features = [];
        const response = await this._make_post_request(params, request_url);
        if (response.ok) {
            const r_clone = response.clone();
            const bufferFeature = await response.json();
            if (bufferFeature.geometries) {
                bufferFeature.geometries.forEach((geometry) => {
                    const polygon = new Polygon(geometry.rings, GeometryLayout.XY);
                    const feature = new Feature(polygon);
                    features.push(feature);
                });
                return features[0];
            }
            else {
                return await r_clone.json();
            }
        }
        else {
            let err = {
                error: {
                    code: response.status,
                    message: response.statusText,
                    details: []
                }
            };
            return err;
        }
    }
    async get_feature_by_id(id) {
        let result = await this._request_features_by_ids([id]);
        return result.map(a => a[0]);
    }
    async get_features_by_ids(ids) {
        if (ids.length === 0)
            return None.instance();
        if (ids.length <= 1000) {
            let features = await this._request_features_by_ids(ids);
            return features;
        }
        const id_matrix = chunk(ids, 1000);
        let optional_collection = [];
        await async_forEach(id_matrix, async (id_chunk) => {
            const batch = await this._request_features_by_ids(id_chunk);
            optional_collection.push(batch);
        });
        let features = Maybe(optional_collection.flatMap(ofs => ofs.unwrapOr([])));
        return features;
    }
    async get_feature_at_point(point) {
        const feature = await this._request_feature_at_point(point);
        return feature;
    }
    async get_features_by_geometry(geometry, idfield = '', request_url = '') {
        let features = await this._request_features_by_geometry(geometry, idfield, request_url);
        return features;
    }
    async get_neighbours_by_geometry(geometries = [], request_url = '') {
        let features = await this._request_neighbors_by_geometry(geometries, request_url);
        return features;
    }
    async get_buffer_by_geometry(geometries = [], request_url, options) {
        let features = await this._request_buffer_by_geometry(geometries, request_url, options);
        return features;
    }
}
